/**
 * `Disc` — the dubplate. `docs/design-direction.md` §6.4, §7.2 ROTATION and NEEDLE
 * DROP.
 *
 * Zero image assets: the grooves are a `repeating-radial-gradient`, the label is a
 * 46%-diameter circle exactly as a real 7" is cut, and the spindle is a hole. All
 * three marks are the global `.disc` / `.disc__label` / `.disc__spindle` recipes in
 * `styles/texture.css`, and the 1333ms revolution (45 RPM, matching the rim print) is
 * `styles/motion.css`. This component supplies the box, the children and one piece of
 * state.
 *
 * **THE FREEZE IS THE SIGNATURE.** On pause the rotation is *paused*, never removed:
 * `animation-play-state: paused` leaves the record standing where the needle left it,
 * so the scrawl sits at a different angle every time and no two screenshots of this
 * app are ever the same (§14). Writing `animation: none`, re-keying the element, or
 * clearing a transform would snap the label back to 0° and delete that — so the state
 * is expressed as **one inline `animation-play-state`** and this component never
 * touches `transform`, `animation-name` or the element's key. The inline declaration
 * is deliberate rather than the `[data-playing]` selector alone: it is the form that
 * cannot be lost to a cascade order and the form a test with no CSS engine can read.
 * `data-playing` is still written, because `motion.css` gates the rim text's
 * counter-rotation on it.
 *
 * Two animations, two elements. A needle drop scales the whole record while the
 * record is turning, and `animation` is one property: the outer `.mount` carries the
 * one-shot scale, the inner `.disc` carries the revolution, and neither can cancel
 * the other. **The drop is one-shot and never plays on resume** (§7.2) — the owner
 * mounts `needleDrop` for a track change and takes it off again.
 *
 * THE PULL UP (§7.3) belongs to the Now Playing screen, not here: it needs the audio
 * element, the haptics and the `PULL UP!` stamp. What this component owes it is a
 * drag surface — `touch-action: none` on the mount (or Telegram's sheet-dismiss
 * gesture steals the drag), the pointer handlers passed straight through, and a
 * forwarded `ref` so the gesture can write `--angle` at pointer rate without a React
 * render per frame.
 */

import type { DOMAttributes, JSX, ReactNode, Ref } from 'react';

import styles from './components.module.css';
import { cx } from './primitives/Rule';

/** The pointer surface THE PULL UP (§7.3) attaches to. Nothing else is forwarded. */
export type DiscPointerHandlers = Pick<
  DOMAttributes<HTMLDivElement>,
  'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel' | 'onLostPointerCapture'
>;

export interface DiscProps {
  /** Drives `animation-play-state`. A pause freezes the angle; it never resets it. */
  readonly playing: boolean;
  /** The label print: a `PlateLabel`, a `RimText`, or nothing while metadata loads. */
  readonly children?: ReactNode;
  /**
   * One-shot NEEDLE DROP (§7.2): scale `.94 → 1` over `--tdrop`. Play-from-stopped
   * only — a resume must not re-drop the needle.
   */
  readonly needleDrop?: boolean;
  /** The drag surface for THE PULL UP. */
  readonly pointer?: DiscPointerHandlers | undefined;
  /** Forwarded to the drag surface so a gesture can write custom properties on it. */
  readonly ref?: Ref<HTMLDivElement> | undefined;
  readonly className?: string | undefined;
}

export function Disc({
  playing,
  children,
  needleDrop = false,
  pointer,
  ref,
  className,
}: DiscProps): JSX.Element {
  return (
    <div
      ref={ref}
      className={cx(styles.mount, className)}
      data-needle-drop={needleDrop ? 'true' : undefined}
      {...pointer}
    >
      <div
        className="disc"
        data-playing={playing ? 'true' : 'false'}
        /*
         * The ONLY style this component writes. `transform` is absent on purpose:
         * `texture.css` sets the resting `translateX(-8px)` and the `bl-spin`
         * keyframes carry it through the revolution, so writing one here would both
         * fight the keyframe and reset the freeze angle.
         */
        style={{ animationPlayState: playing ? 'running' : 'paused' }}
      >
        {children}
        <span className="disc__spindle" aria-hidden="true" />
      </div>
    </div>
  );
}
